import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { ICONS } from '@core/services/icons/icons.service';

import {
  OccupationSeries,
  OccupationState,
  occupationState
} from '../models/occupation.model';

interface OccupationRow {
  siteUid: string;
  name: string;
  capacity: string;
  peak: string;
  peakUtilization: string;
  meanUtilization: string;
  busy: string;
  over: string;
  state: OccupationState;
}

const NOT_APPLICABLE = '—';

function ratio(value: number | null): string {
  return value === null ? NOT_APPLICABLE : `${Math.round(value * 100)}%`;
}

function duration(ms: number): string {
  if (ms <= 0) return NOT_APPLICABLE;
  const minutes = Math.round(ms / 60_000);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours > 0
    ? `${hours}h${rest ? String(rest).padStart(2, '0') : ''}`
    : `${minutes}m`;
}

@Component({
  selector: 'app-occupation-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  templateUrl: './occupation-table.component.html',
  styles: ``
})
export class OccupationTableComponent {
  protected readonly ICONS = ICONS;
  readonly series = input.required<OccupationSeries[]>();

  protected readonly rows = computed<OccupationRow[]>(() =>
    this.series().map((s) => ({
      siteUid: s.siteUid,
      name: s.infrastructureName,
      capacity: s.capacity === null ? NOT_APPLICABLE : String(s.capacity),
      peak: String(s.peak),
      peakUtilization: ratio(s.peakUtilization),
      meanUtilization: ratio(s.meanUtilization),
      busy: duration(s.busyMs),
      over: duration(s.overMs),
      state: occupationState(s)
    }))
  );

  /** One distinct icon per state: status is never carried by color alone. */
  protected stateIcon(state: OccupationState): string {
    if (state === 'over') return ICONS.warning;
    if (state === 'tight') return ICONS.warningCircle;
    if (state === 'unknown') return ICONS.questionMark;
    if (state === 'unreported') return ICONS.prohibit;
    return ICONS.checkCircle;
  }
}
